import { MILLISECONDS_IN_MINUTE } from '@stdlib/constants-time';
import type { QueryKey, UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type {
  PackReviewsResponse,
  SearchReviewResponse,
} from 'app/typings/core/extranet/reviews/reviews';
import { apiRequest } from 'app/utils/request/api';

import { CORE_QUERY_KEYS, ENDPOINTS } from '.';

export type ReviewFilters = {
  pageNumber?: number;
  search?: string;
  beginning?: string;
  ending?: string;
  sortingColumn?: string;
  sortingOrder?: 'ASC' | 'DESC';
  packs?: { isLegacy: boolean; packId: number }[];
};

export function useReviews(hotelId: number, queryParams: ReviewFilters = {}) {
  const formattedQueryParams = useMemo(() => {
    return {
      ...queryParams,
      packs: queryParams.packs ? JSON.stringify(queryParams.packs) : undefined,
    };
  }, [queryParams]);

  const key = useMemo(() => {
    return [CORE_QUERY_KEYS.reviews, { ...formattedQueryParams }];
  }, [formattedQueryParams]);

  return useQuery({
    queryKey: key,
    queryFn: () =>
      apiRequest({
        path: ENDPOINTS.getReviews,
        params: { id: hotelId?.toString() },
        queryParams: formattedQueryParams,
      }) as Promise<SearchReviewResponse>,
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
  });
}

export function useReviewsByPack(
  hotelId?: number,
  options?: Omit<
    UseQueryOptions<
      PackReviewsResponse | null,
      unknown,
      PackReviewsResponse | null,
      QueryKey
    >,
    'queryKey' | 'queryFn'
  >
) {
  return useQuery<PackReviewsResponse | null>({
    queryKey: [CORE_QUERY_KEYS.reviewsByPack, { id: hotelId }],
    queryFn: () =>
      hotelId
        ? (apiRequest({
            path: ENDPOINTS.getReviewsByPack,
            params: {
              id: hotelId.toString(),
            },
          }) as Promise<PackReviewsResponse>)
        : Promise.resolve(null),
    staleTime: 5 * MILLISECONDS_IN_MINUTE,
    enabled: !!hotelId,
    ...options,
  });
}
